import WavesurferPlayer from '@wavesurfer/react';
import chroma from 'chroma-js';
import { Alert, Button, Label, Spinner, TextInput, Tooltip } from 'flowbite-react';
import { useAtom, useAtomValue } from 'jotai';
import { isNumber } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import WaveSurfer from 'wavesurfer.js';
import Minimap from 'wavesurfer.js/dist/plugins/minimap';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';
import TimelinePlugin from "wavesurfer.js/dist/plugins/timeline";
import { useTheme } from './ThemeProvider';

import { firstBeatInSegment, secToBeat, toFixedNumber } from 'lib/maths-utils';
import { FaCirclePause, FaCirclePlay } from 'react-icons/fa6';
import { activePresetInfoAtom, audioBeatDataAtom, audioEventDataAtom, audioFilenameAtom, audioSegmentEndAtom, audioSegmentStartAtom, audioTotalDurationAtom, audioUrlAtom, fpsAtom, isEstimatingTempoAtom, isMobileAudioEditorOpenAtom, requiredAudioFieldsAtom } from './atoms/animationFormAtoms';

import { analyzeAudioFile } from 'app/animate/[[...id]]/ServerActions';

import { MIN_AUDIO_DURATION } from '@/lib/config';
import { classNames } from '@/lib/generic-utils';

import { detectEvents, truncateAndConvertToMonoWithBalance } from '@/lib/audioprocessing-client-utils';
import { dataUrlToAudioBuffer } from '@/lib/client-utils';
import { log } from '@logtail/next';
import { useDebounceEffect } from 'ahooks';
import {
    CategoryScale,
    Chart as ChartJS,
    Colors,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Title,
    Tooltip as TooltipChartJS
} from 'chart.js';
import 'chartjs-adapter-moment';
import annotationPlugin from 'chartjs-plugin-annotation';
import _ from 'lodash';
import { createPortal, unstable_batchedUpdates } from 'react-dom';


ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    TooltipChartJS,
    Legend,
    Colors,
    TimeScale,
    annotationPlugin
)

type SliderProps = {
    id: string,
    label: string,
    value: number,
    setValue: (val: number) => void,
    min: number,
    max: number,
    step: number,
    rounding?: number,
    extraInfo: string
    suffix?: string
    leftComponent?: React.ReactNode
    rightComponent?: React.ReactNode
};

const Slider = ({ id, label, value, setValue, min, max, step, extraInfo, leftComponent, rightComponent, rounding }: SliderProps) => {
    const [inputValue, setInputValue] = useState<string>(value.toString());
    const [previousValue, setPreviousValue] = useState<number>(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();

    useEffect(() => {
        setInputValue(toFixedNumber(value, rounding??3).toString());
    }, [value, rounding]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleInputBlur = () => {
        const parsedValue = parseFloat(inputValue);
        if (!isNaN(parsedValue) && parsedValue >= min && parsedValue <= max) {
            setValue(toFixedNumber(parsedValue, rounding??3));
        } else {
            setInputValue(value.toString());
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setInputValue(previousValue.toString());
        } else if (e.key === 'Enter') {
            const parsedValue = parseFloat(inputValue);
            if (!isNaN(parsedValue) && parsedValue >= min && parsedValue <= max) {
                setValue(parsedValue);
                setPreviousValue(parsedValue);
            } else {
                setInputValue(previousValue.toString());
            }
        }
    };

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = parseFloat(e.target.value);
        setValue(newValue);
        setPreviousValue(newValue);
    };

    return (
        <div className='flex items-center w-full'>
            <Label htmlFor={id} className="mr-4 text-neutral-700 dark:text-neutral-400 whitespace-nowrap">
                {label}: <span className='font-light'>{extraInfo}</span>
            </Label>
            <div className="flex flex-col w-full">
                <div className="flex gap-2 items-center justify-between w-full">
                    {leftComponent??<></>}
                    <input
                        type="range"
                        id={id}
                        name={id}
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={handleSliderChange}
                        className="w-full h-2 bg-blue-400 rounded-none appearance-none cursor-pointer [&::-webkit-slider-thumb]:rounded-none"
                    />
                    <TextInput
                            type="text"
                            id={`${id}-text`}
                            name={`${id}-text`}
                            value={inputValue}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            onKeyDown={handleInputKeyDown}
                            ref={inputRef}
                            className="rounded-none"
                            style={{
                                borderRadius: '0',
                                padding: '1px',
                                background: theme.theme === 'dark' ? 'rgba(0, 0, 0)' : 'rgba(255, 255, 255)',
                                color: theme.theme === 'dark' ? 'rgb(180,180,180)' : 'rgb(80,80,80)',
                                textAlign: 'center',
                                border: theme.theme === 'dark' ? '1px solid rgb(180,180,180)' : '1px solid rgb(0,0,0)',
                            }}
                            size={8}
                            height={4}
                            sizing={'small'}
                        />
                    {rightComponent??<></>}
                </div>
            </div>
        </div>
    );
};


const AnnotatedWaveform = ({maxAudioDurationForUser} : {maxAudioDurationForUser:number}) => {

    const audioUrl = useAtomValue(audioUrlAtom);
    const [audioSegmentStart, setAudioSegmentStart] = useAtom(audioSegmentStartAtom);
    const [audioSegmentEnd, setAudioSegmentEnd] = useAtom(audioSegmentEndAtom);
    const [audioEventData, setAudioEventData] = useAtom(audioEventDataAtom);
    const [audioBeatData, setAudioBeatData] = useAtom(audioBeatDataAtom);
    const audioFilename = useAtomValue(audioFilenameAtom);
    const [audioTotalDuration, setAudioTotalDuration] = useAtom(audioTotalDurationAtom);
    
    // Transient state – this is state shared by components but not persisted to the DB 
    const [isEstimatingTempo, setIsEstimatingTempo] = useAtom(isEstimatingTempoAtom)
    const [, setVisibleTime] = useState<{start: number, end: number}>({start:0, end:0})
    const [visibleDuration, setVisibleDuration] = useState<number>(audioTotalDuration)

    const [eventSensitivity, setEventSensitivity] = useState<number>(0.5);
    const [detectedEventCount, setDetectedEventCount] = useState<number>(0);
    const [eventShift, setEventShift] = useState<number>(0);
    const [zoom, setZoom] = useState<number>(100);

    const theme = useTheme();
    const [isPlaying, setIsPlaying] = useState<boolean>(false);

    const [isDetectingEvents, setIsDetectingEvents] = useState<boolean>(false);

    const [playheadPos, setPlayheadPos] = useState<number>(0);

    const beatIndicatorRef = useRef<HTMLDivElement>(null);
    const eventIndicatorRef = useRef<HTMLDivElement>(null);

    const [ws, setWs] = useState<WaveSurfer | null>(null);
    const wsRegionsRef = useRef<RegionsPlugin | null>(null);
    const waveformContainerRef = useRef<HTMLDivElement>(null);

    const [isCalibratingTempo, setIsCalibratingTempo] = useState<number>(0);
    const [tempoCalibrationPos1, setTempoCalibrationPos1] = useState<number>(0);

    const [fps] = useAtom(fpsAtom);

    const [detectedOnsetsSortedByStrength, setDetectedOnsetsSortedByStrength] = useState<{t:number, strength:number}[]>([]);
    const [detectedOnsetsEventsToIgnore, setDetectedOnsetsEventsToIgnore] = useState<number[]>([]);
    const [detectedOnsetEventsToShow, setDetectedOnsetEventsToShow] = useState<number[]>([]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isEventDeletionMode, setEventDeletionMode] = useState<boolean>(false);

    const activePreset = useAtomValue(activePresetInfoAtom);
    const requiredAudioFields = useAtomValue(requiredAudioFieldsAtom);

    const [enableEvents, setEnableEvents] = useState<boolean>(false);

    const [swimlineHeight, setSwimlineHeight] = useState<number | null>(null);

    useEffect(() => {
        if (waveformContainerRef.current) {
            const computedStyle = getComputedStyle(waveformContainerRef.current);
            const height = computedStyle.getPropertyValue('--event-swimline-height');
            try {
                const heightPx = parseFloat(height.trim().replace('px', ''));
                setSwimlineHeight(heightPx); // `trim` to remove any surrounding whitespace
            } catch {
                log.debug("Failed to parse swimline height from CSS variable", { height });
            }
        }
    }, []);

    const waveformColours = useMemo(() => ({
        waveColour: theme.theme === 'dark' ? ['rgb(180, 180, 180)', 'rgb(80, 80, 80)'] : ['rgba(80, 80, 80)', 'rgba(140, 140, 140)'],
        progressColour: theme.theme === 'dark' ? 'rgb(110, 110, 110)' : 'rgb(160, 160, 160)',
        manualEventColour: theme.theme === 'dark' ? 'rgba(100, 255, 100, 0.6)' : 'rgba(0, 155, 0, 0.6)',
        detectedEventColour: 'rgba(0, 255, 0, 0.6)',
        seltectedEventColour: theme.theme === 'dark' ? 'rgba(128, 255, 128, 1)' : 'rgba(128, 255, 0, 1)',
        beatMeasureColour: 'rgba(119, 169, 250, 1)',
        beatBarColour: 'rgba(119, 169, 250, 0.8)',
        beatColour: 'rgba(119, 169, 250, 0.35)',
        beforeAnimationColour: theme.theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
        afterAnimationColour: theme.theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
        playheadColour: 'rgba(255, 0, 0, 1)',
        minimapOverlayColour: theme.theme === 'dark' ? 'rgba(255, 255, 255, 0.10)' : 'rgba(0, 0, 0, 0.10)',
    }), [theme.theme]);

    const [isMobile, setIsMobile] = useState(false);
    const [isMobileAudioEditorOpen, setIsMobileAudioEditorOpen] = useAtom(isMobileAudioEditorOpenAtom);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768 || window.innerHeight < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);


    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (isMobileAudioEditorOpen && (!event.state || !event.state.modal)) {
                setIsMobileAudioEditorOpen(false);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isMobileAudioEditorOpen, setIsMobileAudioEditorOpen]);


    // TODO: This needs to be function rat her than an effect because we need to call it manually 
    // when regions are added or removed in WS. Ideally we'd invert the source of truth away from the WS datastructure.
    const updateEventStateFromRegionData = useCallback(() => {
        if (!wsRegionsRef.current || !ws)
            return;
        // Digest regions into the values we'll send to the server for animation.
        const eventsForSubmission = wsRegionsRef.current.getRegions()
            .filter(r => r.id.includes('event')
                && r.start >= (audioSegmentStart ?? 0)
                && r.end < (audioSegmentEnd ?? ws.getDuration())).map(r => {
                    return {
                        time: r.start - (audioSegmentStart ?? 0),
                        label: r.id,
                        strength: 1 // TODO - per event strength
                    }
                });

        setAudioEventData(eventsForSubmission);
        log.debug("Events for submission: " + eventsForSubmission.length, {audioSegmentStart, audioSegmentEnd, eventsForSubmission});
        
    }, [audioSegmentEnd, audioSegmentStart, setAudioEventData, ws])

    // As described above, this should ideally be a useDebounceEffect only, but it needs to be invoked manually in some cases.
    useDebounceEffect(() => {
        updateEventStateFromRegionData();
    }, [updateEventStateFromRegionData], {wait: 200, leading: true, trailing: true, maxWait: 400});


    // Re-draw markers for detected events
    useDebounceEffect(() => {
        if (!ws || !wsRegionsRef.current) {
            return;
        }
        wsRegionsRef.current.getRegions().filter(r => r.id.includes('detected')).forEach(r => r.remove());
        for (const [idx, time] of detectedOnsetEventsToShow.entries()) {
            wsRegionsRef.current?.addRegion({
                start: time + eventShift,
                color: waveformColours.detectedEventColour,
                drag: true,
                id: `detected event ${idx}`
            });
        }
        setDetectedEventCount(detectedOnsetEventsToShow.length);
        updateEventStateFromRegionData();
    }, [detectedOnsetEventsToShow, waveformColours.detectedEventColour, eventShift, updateEventStateFromRegionData], {wait: 40, leading: true, trailing: true, maxWait: 40});

    // Add fixed beat markers at regular itervals based on tempo
    useDebounceEffect(() => {
        if (!ws || !wsRegionsRef.current || !audioBeatData) {
            return;
        }
        wsRegionsRef.current.getRegions().filter(r => r.id.includes('beat')).forEach(r => r.remove());
        let beatPos = audioBeatData.offset;
        let beatIdx = 0;
        const beatsPerBar = 4; //TODO - make configurable
        const beatsPerMeasure = 16; //TODO - make configurable

        while (beatPos < ws.getDuration()) {
            const beatType = (beatIdx % beatsPerMeasure === 0) ? 'measure' : (beatIdx % beatsPerBar === 0) ? 'bar' : 'minorbeat';
            wsRegionsRef.current?.addRegion({
                start: beatPos,
                color: waveformColours.beatColour,
                drag: false,
                id: `beat ${beatType} ${beatIdx}`,
            });
            
            beatPos += 60 / audioBeatData.bpm;
            beatIdx++;
        }
        
    }, [wsRegionsRef, audioBeatData, visibleDuration, waveformColours.beatColour, ws]
    , {wait: 20, leading: true, trailing: true, maxWait: 20}
    );

    // Make beat markers full height if they are sufficiently spaced.
    useEffect(() => {
        if (!ws || !wsRegionsRef.current || !audioBeatData) {
            return;
        }        
        const pixelsPerSecond = (ws?.getWrapper()?.parentElement?.clientWidth ?? 0) / visibleDuration;
        const pixelsPerBeat = pixelsPerSecond * (60 / audioBeatData.bpm);

        if (pixelsPerBeat>32) {
            waveformContainerRef.current?.classList.add('full-beat-mode');
        } else {
            waveformContainerRef.current?.classList.remove('full-beat-mode');
        }
    }, [ws, visibleDuration, audioBeatData]);

    ////////////////////////////////////////////////////
    // Wavesurfer event listener updates
    ///////////////////////////////////////////////////
    useEffect(() => {
        if (!ws || !audioBeatData) {
            return;
        }

        const unsubscribe = ws.on('timeupdate', (currentTime : number) => {
            const lastEventPos = wsRegionsRef.current?.getRegions()
                .filter(r => r.id.includes('event'))
                .sort((a, b) => a.start - b.start)
                .findLast(r => r.start < currentTime)?.start ?? 0;
            const timeSinceEvent = currentTime - lastEventPos;
            const eventLight = Math.min(1, Math.max(0.2, (0.5 - timeSinceEvent) * 2));
            if (eventIndicatorRef.current) {
                eventIndicatorRef.current.style.backgroundColor = chroma(waveformColours.manualEventColour).alpha(eventLight).css();
                eventIndicatorRef.current.style.height = (100 * eventLight) + '%';
                eventIndicatorRef.current.style.borderRadius = (100 * (1-eventLight)) + '%' ;
            }

            const secPerBeat = 60 / audioBeatData?.bpm;
            const beatsSoFar = (currentTime - audioBeatData?.offset) / secPerBeat;
            const lastBeat = Math.floor(beatsSoFar);
            const lastBeatInS = lastBeat * secPerBeat + audioBeatData?.offset;
            const timeSinceBeat = currentTime - lastBeatInS;
            const beatLight = Math.min(1, Math.max(0.2, (0.5 - timeSinceBeat) * 2));
            if (beatIndicatorRef.current) {
                beatIndicatorRef.current.style.backgroundColor = chroma(waveformColours.beatMeasureColour).alpha(beatLight).css();
                beatIndicatorRef.current.style.height = (100 * beatLight) + '%';
                beatIndicatorRef.current.style.borderRadius = (100 * (1-beatLight)) + '%' ;
            }

            // TODO: consider debouncing
            setPlayheadPos(currentTime); 
        });

        return unsubscribe;

    }, [audioBeatData, waveformColours.beatMeasureColour, waveformColours.manualEventColour, ws]);


    useEffect(() => {
        if (!ws || !wsRegionsRef.current) {
            return;
        }        
        const unsubscribe = wsRegionsRef.current.on('region-updated', (r) => {
            // If a detected event is moved, it becomes a manual event.
            if (r.id.includes('event')) {
                r.id = `manual event ${_.last(r.id.split(' '))}`; 
                r.color = waveformColours.manualEventColour;
                r.remove();
                wsRegionsRef.current?.addRegion(r);
            }

            // update stored events
            updateEventStateFromRegionData();
        });
        return unsubscribe
    }, [updateEventStateFromRegionData, waveformColours.manualEventColour, ws]);

    useEffect(() => {
        if (!ws || !wsRegionsRef.current) {
            return;
        }        

        const unsubscribe = wsRegionsRef.current?.on('region-clicked', (clickedRegion, mouseEvent) => {
            if (!mouseEvent.target) {
                return;
            }
            try {
                const boundingRect = (mouseEvent.target as HTMLDivElement).getBoundingClientRect();
                const distanceBelowTop = mouseEvent.clientY - boundingRect.top;
                const distanceAboveBottom = boundingRect.height - distanceBelowTop;
                const regionId = clickedRegion.id;
                if (regionId.includes('event')) {
                    if ((mouseEvent.shiftKey || distanceAboveBottom < 0)) {
                        wsRegionsRef.current?.getRegions().find(r => r.id === regionId)?.remove();
                        setDetectedOnsetsEventsToIgnore(detectedOnsetsEventsToIgnore => [...detectedOnsetsEventsToIgnore, clickedRegion.start - eventShift ])
                    }
                    if (mouseEvent.cancelable) mouseEvent.preventDefault();
                    mouseEvent.stopPropagation();
                }
            } catch (error) {
                console.error("Error handling region click", error);
            }

        });

        return unsubscribe;
    }, [ws, eventShift]);




    /////////////////////////////////////////////

    useEffect(() => {
        const newEnableEvents = !!requiredAudioFields?.includes('event');
        setEnableEvents(newEnableEvents);
        if (newEnableEvents) {
            waveformContainerRef.current?.classList.add('event-mode');
        } else {
            waveformContainerRef.current?.classList.remove('event-mode');
        } 
    }, [requiredAudioFields])

    const playPause = useCallback((offset?: number) => {
        if (ws) {
            if (ws.isPlaying()) {
                setIsPlaying(false);
                ws.pause();
            } else {
                if (isNumber(offset)) {
                    ws.seekTo(offset);
                }
                ws.play()
                    .then(() => setIsPlaying(true))
                    .catch(() => ws.pause());
            }
        }
    }, [ws]);

    useHotkeys('space',
        () => playPause(),
        { preventDefault: true, description: 'Play from cursor position / pause.' },
        [playPause]);

    useHotkeys('ctrl+space',
        () => playPause((audioSegmentStart ?? 0) / (audioTotalDuration || 1) ),
        { preventDefault: true, description: 'Play from start of segment to animate.' },
        [playPause, audioSegmentStart]);
    
    useHotkeys('shift',
        (k) => {
            if (k.type === 'keydown') {
                setEventDeletionMode(true);
                waveformContainerRef.current?.classList.add('event-deletion-mode');
            } else {
                setEventDeletionMode(false);
                waveformContainerRef.current?.classList.remove('event-deletion-mode');
            }
        },
        { keydown: true, keyup: true, preventDefault: false, description: 'Enable event deletion' },
        [wsRegionsRef, waveformColours]);


        useHotkeys('ctrl+e',
            () => { 
                if (enableEvents) {
                    wsRegionsRef.current?.addRegion({
                        start: playheadPos,
                        color: waveformColours.manualEventColour,
                        drag: true, 
                        id: `manual event ${wsRegionsRef.current?.getRegions().length ?? 0}`,
                    });
                    updateEventStateFromRegionData();
                }
            },
            {preventDefault: false, description: 'Add event at current cursor pos.' },
            [wsRegionsRef, waveformColours, playheadPos, ws]);
    


    useEffect(() => {
        const eventsToKeep = Math.max(Math.round(detectedOnsetsSortedByStrength.length * eventSensitivity), 1);
        const filteredOnsets = detectedOnsetsSortedByStrength.slice(0, eventsToKeep).filter(o =>!detectedOnsetsEventsToIgnore.includes(o.t));
        const filteredOnsetsSortedByTime = filteredOnsets.sort((a, b) => a.t - b.t);
        setDetectedOnsetEventsToShow(filteredOnsetsSortedByTime.map(o => o.t));
        updateEventStateFromRegionData();
    }, [detectedOnsetsSortedByStrength, eventSensitivity, detectedOnsetsEventsToIgnore, updateEventStateFromRegionData]);

    // eslint-disable-next-line @typescript-eslint/require-await
    const estimateEvents = useCallback(async function (method:'energy'|'amplitude' = 'amplitude') {
        if (!audioUrl || !isNumber(audioSegmentStart) || !isNumber(audioSegmentEnd) || !isNumber(audioBeatData?.bpm)) {
            return;
        }

        wsRegionsRef.current?.getRegions().filter(r => r.id.includes('detected')).forEach(r => r.remove());
        unstable_batchedUpdates(() => {
            setDetectedOnsetsSortedByStrength([]);
            setDetectedOnsetsEventsToIgnore([]);
            setEventSensitivity(0.5);
            setEventShift(0);
            setIsDetectingEvents(true);
        });

        const truncatedDuration = audioSegmentEnd - audioSegmentStart;
        const startTime = window.performance.now();
        try {
            
            // TODO - use pre-decoded audiobuffer if available
            const audioBuffer = await dataUrlToAudioBuffer(audioUrl);

            const audioContext = new AudioContext();
            const targetSamples = fps * truncatedDuration;
            
            // TODO: this may be movable to derived state, else we duplicate on submit.
            const {monoBuffer} = truncateAndConvertToMonoWithBalance(audioContext, audioBuffer, audioSegmentStart, audioSegmentEnd);        
            const {events, error} = await detectEvents(monoBuffer, targetSamples, audioBeatData?.bpm, 4, method);
            if (error) {
                throw new Error("Error in client-side event detection: " + error);
            }
            const eventsInSegment = events.map(e => ({...e, t: audioSegmentStart + e.t}));
            setDetectedOnsetsSortedByStrength(eventsInSegment.sort((a, b) => b.strength - a.strength));
        } catch (error) {
            log.error("Browser error: Error detecting events", {error});
            // TODO: Currently no error is show to user.
        } finally {
            const timing = window.performance.now() - startTime;
            log.info(`Client-side event detection complete: ${truncatedDuration}s analysed in ${timing}ms.`, {clientEventDetectionMs: timing, duration: truncatedDuration, engine: 'amplitude_gate'});
            setIsDetectingEvents(false);
        }
    
    }, [audioBeatData?.bpm, audioSegmentEnd, audioSegmentStart, audioUrl, fps]);

    // We're not using aubio for event detection but we might revive it one day.
    // const estimateEventsAubio = useCallback(async () => {
    //     if (!audioUrl || !isNumber(audioSegmentStart) || !isNumber(audioSegmentEnd) || !isNumber(audioBeatData?.bpm)) {
    //         return;
    //     }

    //     wsRegionsRef.current?.getRegions().filter(r => r.id.startsWith('event')).forEach(r => r.remove());
    //     unstable_batchedUpdates(() => {
    //         setDetectedOnsetsSortedByStrength([]);
    //         setDetectedOnsetsEventsToIgnore([]);
    //         setEventSensitivity(0.5);
    //         setEventShift(0);
    //         setIsDetectingEvents(true);
    //     });

    //     const truncatedDuration = audioSegmentEnd - audioSegmentStart;
    //     const startTime = window.performance.now();
    //     try {
    //         // TODO - use pre-decoded audiobuffer if available
    //         const audioBuffer = await dataUrlToAudioBuffer(audioUrl);
    //         const audioContext = new AudioContext();

    //         // TODO: this may be movable to derived state, else we duplicate on submit.
    //         const {monoBuffer} = truncateAndConvertToMonoWithBalance(audioContext, audioBuffer, audioSegmentStart, audioSegmentEnd);    
    //         const channelData = monoBuffer.getChannelData(0);

    //         const onsetInitData : OnsetWorkerInput = {
    //             buffer: channelData,
    //             sampleRate: audioBuffer.sampleRate,
    //             bufferSize: 512,
    //             hopSize: 256,
    //             method: 'energy',
    //             slience: -80,
    //             threshold: 10,
    //         };

    //         const onsetDetectionWorker = new Worker(new URL('../../webworkers/analysisWorker-onset.ts', import.meta.url));
    //         const events : {t:number, strength:number}[] = [];
            
    //         const minGapS = (1/fps) * 2; // Require at least 2 frames gap between events.
    //         let lastEventS = -1;
    //         onsetDetectionWorker.onmessage = (e : MessageEvent<OnsetWorkerOutput> ) : any => {
    //             if (e.data.status === 'done') {
    //                 unstable_batchedUpdates(() => {
    //                     setIsDetectingEvents(false);
    //                     setDetectedOnsetsSortedByStrength(events.sort((a, b) => b.strength - a.strength));
    //                 });
    //                 onsetDetectionWorker.terminate();
    //             } else {
    //                 if (e.data.eventS  > lastEventS + minGapS) {
    //                     const samplePos = Math.round(e.data.eventS * audioBuffer.sampleRate);
    //                     events.push({
    //                         t: e.data.eventS + audioSegmentStart,
    //                         strength: channelData[samplePos]
    //                     });
    //                     lastEventS = e.data.eventS;
    //                 }
    //             }
    //         }

    //         onsetDetectionWorker.onerror = (error) => {
    //             log.error("Browser error: Error detecting events", {error});
    //             setIsDetectingEvents(false);
    //         }

    //         onsetDetectionWorker.postMessage(onsetInitData);

    //     } catch (error) {
    //         log.error("Browser error: Error detecting events (aubio)", {error});
    //         // TODO: Currently no error is show to user.
    //     } finally {
    //         const timing = window.performance.now() - startTime;
    //         log.info(`Client-side event detection complete: ${truncatedDuration}s analysed in ${timing}ms.`, {clientEventDetectionMs: timing, duration: truncatedDuration, engine: 'aubio'});
    //     }


    // }, [audioBeatData?.bpm, audioSegmentEnd, audioSegmentStart, audioUrl, fps]);

    // We're not using librosa for event detection but we might revive it one day.
    // const estimateEventsLibrosa = useCallback(() => {
    //     if (!audioUrl || !isNumber(audioSegmentStart) || !isNumber(audioSegmentEnd) || !isNumber(audioBeatData?.bpm)) {
    //         return;
    //     }

    //     wsRegionsRef.current?.getRegions().filter(r => r.id.startsWith('event')).forEach(r => r.remove());
    //     unstable_batchedUpdates(() => {
    //         setDetectedOnsetsSortedByStrength([]);
    //         setDetectedOnsetsEventsToIgnore([]);
    //         setEventSensitivity(0.5);
    //         setEventShift(0);
    //         setIsDetectingEvents(true);
    //     });
        
    //     try {
    //         const events = librosaEvents.filter(e => e.t >= audioSegmentStart && e.t <= audioSegmentEnd);
    //         setDetectedOnsetsSortedByStrength(events.sort((a, b) => b.strength - a.strength));
    //     } catch (error) {
    //         log.error("Browser error: Error detecting events (librosas)", {error});
    //         // TODO: Currently no error is show to user.
    //     } finally {
    //         setIsDetectingEvents(false);
    //     }

    // }, [audioBeatData?.bpm, audioSegmentEnd, audioSegmentStart, audioUrl, librosaEvents]);


    // TODO - partially duplicated in AnimationForm.tsx
    const estimateTempo = useCallback(async (_:React.MouseEvent) => {
        if (!audioFilename || !audioUrl) {
            setAudioBeatData(undefined);
            return false;
        }
        try {
            setIsEstimatingTempo(true);
            const startTime = window.performance.now();
            
            // Start remote and local analysis in parallel. 
            // The remote analysis is the source of truth but we want to be abel to compare against the local analysis.
            const remoteResult = await analyzeAudioFile(audioFilename);
            // We are no longer using essentia_estimateTempo for tempo estimation but we might revive it in the future.
            // const localAnalysisPromise = doLocalBpmAnalysis ? essentia_estimateTempo(audioUrl).then(localResult => {
            //     const localEndTime = window.performance.now();
            //     const localAnalysisTime = localEndTime - startTime;
            //     return { result: localResult, time: localAnalysisTime };
            // }) : undefined;
    
            const remoteAnalysisTime = window.performance.now() - startTime;
            log.info(`Remote BPM analysis returned in ${remoteAnalysisTime}ms`)

            if (!remoteResult || !remoteResult.beat_positions || !isNumber(remoteResult.beat_positions[0]) || !isNumber(remoteResult.tempo)) {
                throw new Error("Remote analysis data not of the expected shape.");
            }
            const remoteBeatData = {
                bpm: remoteResult.tempo,
                offset: remoteResult.beat_positions[0] % (60 / remoteResult.tempo)
            };
            setAudioBeatData(remoteBeatData);

        } catch (error) {
            log.error("Browser error: audio analysis failed", {error});
            setAudioBeatData(undefined);
        } finally {
            setIsEstimatingTempo(false);
        }
    }, [audioFilename, audioUrl, setAudioBeatData, setIsEstimatingTempo]);

    const fetchParams = useMemo<RequestInit>(() => {
        return audioUrl?.startsWith('http') ? { "mode": 'cors' } : {};
    }, [audioUrl]);

    const staticPlugins = useMemo(() => [Minimap.create({ overlayColor: waveformColours.minimapOverlayColour })], [waveformColours]);
    
    const audioEventWarning =  useMemo(() => {
        if (!requiredAudioFields.includes('event')) {
            const totalEvents = wsRegionsRef.current?.getRegions().filter(r => r.id.includes('event')).length??0;
            if (totalEvents > 0) {
                return <Alert className='rounded-none m-2' color='info' >
                    <p>{totalEvents} event {totalEvents>1?'s have': ' has'} been placed on the audio track, but the preset <strong>{activePreset?.displayName}</strong> does not use events. {totalEvents>1?'They': 'It'} will be igored.</p>
                </Alert>
            } else {
                return <></>;
            }
        }

        const eventsInSegement = audioEventData?.length??0;
        return <div>
            <Alert className='rounded-none m-2' color={eventsInSegement > 0 ? 'info' : 'warning'} >
                <p>The preset <strong>{activePreset?.displayName}</strong> needs some <strong>events</strong> to work properly. Events placed so far: {eventsInSegement}</p>
            </Alert>
        </div>
    }, [activePreset?.displayName, audioEventData, requiredAudioFields]);

    const positionInfoText =  useMemo(() => {
        if (!ws || !isNumber(audioSegmentStart) || !isNumber(audioSegmentEnd) || !audioBeatData) {
            return <></>
        }

        const segmentLength = audioSegmentEnd - audioSegmentStart;
        
        const playheadPosInBeats = secToBeat(playheadPos - audioBeatData.offset, audioBeatData.bpm);
        const trackEndInBeats = secToBeat(ws.getDuration() ?? - audioBeatData.offset, audioBeatData.bpm)

        const beatOffsetInSegment = firstBeatInSegment(audioBeatData.bpm, audioBeatData.offset, audioSegmentStart).beatOffset;
        const playheadPosInBeatsWithinSegment = secToBeat(playheadPos - audioSegmentStart - beatOffsetInSegment, audioBeatData.bpm);

        const segmentLengthInBeats = secToBeat(segmentLength, audioBeatData.bpm);

        return <div className='font-mono text-xs'>
            <div>
                Selected: {(segmentLength).toFixed(2)}s ({audioSegmentStart.toFixed(2)} - {audioSegmentEnd.toFixed(2)})
            </div>        
            <div>
                Pos: {playheadPos.toFixed(2)}/{ws?.getDuration().toFixed(2) ?? 0}s - Segment[{(playheadPos - audioSegmentStart).toFixed(2)}/{(segmentLength).toFixed(2)}s]
            </div>
            <div>
                Beats:  {playheadPosInBeats.toFixed(2)}/{trackEndInBeats.toFixed(2)}b - Segment[{playheadPosInBeatsWithinSegment.toFixed(2)}/{segmentLengthInBeats.toFixed(2)}b]
            </div>
        </div>

    }, [audioBeatData, audioSegmentEnd, audioSegmentStart, playheadPos, ws]);


    const content = <div>
        {audioEventWarning}
        <div className='flex flex-col md:flex-row items-center justify-between pb-4 w-full'>
            <div className='w-full md:w-auto mb-2 md:mb-0'>
                <Button
                    className='rounded-none items-center justify-center flex flex-row bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white my-1 mx-2 hover:scale-x-110 hover:ease-linear hover:duration-150'
                    onClick={() => playPause()}>
                    <span className='flex flex-row items-center'>
                        {isPlaying ? <><FaCirclePause className='mr-2' />Pause</> : <><FaCirclePlay className='mr-2' />Play</>}
                    </span>
                </Button>
            </div>
            <div className='flex flex-col md:flex-row items-center justify-center w-full md:flex-grow md:max-w-[50%]'>
                <div className='w-full flex flex-row gap-1 items-center mb-2 md:mb-0'>
                    <Slider
                        id="zoom"
                        label="Zoom"
                        value={zoom}
                        setValue={(val) => {
                            setZoom(val);
                            if (ws) {
                                ws.zoom(val);
                            }
                        }}
                        min={10}
                        max={1000}
                        step={0.01}
                        rounding={2}
                        extraInfo=''
                        rightComponent={<Tooltip arrow={false} style="light" content="Fit the animation section to the window.">
                        <Button
                            className='rounded-none p-1 text-xs items-center justify-center flex flex-row bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white my-1 mx-1 hover:scale-x-110 hover:ease-linear hover:duration-150'
                            size='small'
                            onClick={() => {
                                const visiblePixels = ws?.getWrapper()?.parentElement?.clientWidth;
                                if (!visiblePixels || !isNumber(audioSegmentStart) || !isNumber(audioSegmentEnd)) {
                                    return;
                                }

                                const desiredPxPerSec = visiblePixels / (audioSegmentEnd - audioSegmentStart);
                                const seekPosition = audioSegmentStart/ws.getDuration();

                                // Ordering of these calls is important else viewport shuffles unexpectedly.                                        
                                ws.seekTo(seekPosition);
                                ws.zoom(desiredPxPerSec);
                                ws.setScrollTime(audioSegmentStart);

                                setZoom(desiredPxPerSec);
                            }}>
                            Fit
                        </Button></Tooltip>}
                    />
                </div>
            </div>
            <div className='w-full md:w-auto text-right text-xs mt-2 md:mt-0 text-neutral-400'>
                {positionInfoText}
            </div>
        </div>

        <div ref={waveformContainerRef} id='waveform' className={enableEvents?'event-mode':''}>
            <WavesurferPlayer
                url={audioUrl}
                fetchParams={fetchParams}
                waveColor={waveformColours.waveColour}
                progressColor={waveformColours.progressColour}
                cursorColor={waveformColours.playheadColour}                    
                plugins={staticPlugins}
                height={250}
                autoCenter={false}
                onReady={(ws : WaveSurfer) => {
                    const wsRegions = ws.registerPlugin(RegionsPlugin.create());
                    ws.registerPlugin(TimelinePlugin.create());
                    const totalDuration = ws.getDuration();

                    const newAudioSegmentStart = (isNumber(audioSegmentStart) && audioSegmentStart < totalDuration)
                        ? audioSegmentStart : 0;
                    const newAudioSegmentEnd = (isNumber(audioSegmentEnd) && audioSegmentEnd < totalDuration  && audioSegmentEnd > newAudioSegmentStart)
                        ? audioSegmentEnd : Math.min(15, totalDuration);
                    const newSegmentDuration = newAudioSegmentEnd - newAudioSegmentStart;
                    
                    wsRegionsRef.current = wsRegions;
                    const beforeRegion =  wsRegionsRef.current?.addRegion({
                        start: 0,
                        end: newAudioSegmentStart+0.001, //Offset is required so that region is created even with start == 0
                        color: waveformColours.beforeAnimationColour,
                        drag: false, 
                        resize: false,
                        id: `before-animation`,
                    });
            
                    const afterRegion =  wsRegionsRef.current?.addRegion({
                        start: newAudioSegmentEnd-0.01, //Offset is required so that region is created even with end == duration
                        end: ws.getDuration()-0.001,
                        color: waveformColours.afterAnimationColour,
                        drag: false, 
                        resize: false,
                        id: `after-animation`,
                    });
            
                    const dragMarker = document.createElement('div')
                    dragMarker.innerText = `← ${newSegmentDuration.toFixed(2)}s →`;
                    dragMarker.style.textAlign = 'center';
                    dragMarker.style.verticalAlign = 'middle';
                    dragMarker.style.backgroundColor = theme.theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)';
                    dragMarker.style.color = theme.theme === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)';
                    dragMarker.style.fontSize = '0.75em';
                    dragMarker.style.zIndex = '15';
                    dragMarker.style.cursor = 'grab';
                    dragMarker.style.height = isMobile ? '48px' : '24px';
                    dragMarker.classList.add('animation-drag-marker');

                    const addMarker = document.createElement('div');
                    addMarker.classList.add('add-marker');
                    addMarker.style.position = 'absolute';
                    addMarker.style.top = `-${swimlineHeight??48}px`;
                    addMarker.style.left = `-${(swimlineHeight??48)/2}px`;
                    addMarker.style.height = `${swimlineHeight??48}px`;
                    addMarker.style.width = `${swimlineHeight??48}px`;
                    addMarker.style.cursor = 'cell';
                    addMarker.style.zIndex = '5';
                    addMarker.style.pointerEvents = 'all';

                    addMarker.addEventListener('click', (e) => {
                        const seconds = ws.getCurrentTime();
                        wsRegionsRef.current?.addRegion({
                            start: seconds,
                            color: waveformColours.manualEventColour,
                            drag: true, 
                            id: `manual event ${wsRegionsRef.current?.getRegions().length ?? 0}`,
                        });
                        if (e.cancelable) e.preventDefault();
                        e.stopPropagation();
                        updateEventStateFromRegionData();              
                    });
                    const cursorElement = ws.getWrapper().querySelector('.cursor');
                    cursorElement?.appendChild(addMarker);

                    const animationRegion = wsRegions.addRegion({
                        id: 'animation',
                        start: newAudioSegmentStart,
                        end: newAudioSegmentEnd,
                        color: 'rgba(0,0,0,0)',
                        content: dragMarker,
                        drag: true,
                        resize: true,
                        // Allow some elasticity outside of boundaries so we can give feedback about min/max when user tries to drag beyond them.
                        minLength: MIN_AUDIO_DURATION-1, 
                        maxLength: maxAudioDurationForUser+1
                    });

                    // Prevent dragging when in event mode unless dragMarker was the click target.
                    animationRegion.element.addEventListener('pointerdown', (e) => {
                        const isEventMode = document.getElementById('waveform')?.classList.contains('event-mode');
                        const isDragMarker = e.target === dragMarker;
                        if (isEventMode && !isDragMarker) {
                            animationRegion.drag = false;
                        } else {
                            animationRegion.drag = true;
                        }
                    });

                    // Track last positions of the animation region so we can tell what the user was dragging
                    // (shame the regions API doesn't provide this info in the event - TODO wavesurfer contribution)
                    const priorAnimationRegionPositionReference = {
                        start : newAudioSegmentStart,
                        end : newAudioSegmentStart,
                    }

                    animationRegion.on('update', () => {

                        const duration = animationRegion.end - animationRegion.start;

                        // Find out what the user was dragging
                        const startChanged = animationRegion.start !== priorAnimationRegionPositionReference.start;
                        const endChanged = animationRegion.end !== priorAnimationRegionPositionReference.end;


                        if (duration < MIN_AUDIO_DURATION || duration > maxAudioDurationForUser) {

                            // Spring boundary back to min/max if user tries to drag beyond
                            if (startChanged) {
                                if (duration < MIN_AUDIO_DURATION) {
                                    animationRegion.start = animationRegion.end - MIN_AUDIO_DURATION
                                } else if (duration > maxAudioDurationForUser) {
                                    animationRegion.start = animationRegion.end - maxAudioDurationForUser
                                }
                            }
                            if (endChanged) {
                                if (duration < MIN_AUDIO_DURATION) {
                                    animationRegion.end = animationRegion.start + MIN_AUDIO_DURATION
                                } else if (duration > maxAudioDurationForUser) {
                                    animationRegion.end = animationRegion.start + maxAudioDurationForUser
                                }
                            }

                            // Apply changes with warning flash
                            animationRegion.setOptions({
                                start: animationRegion.start,
                                end: animationRegion.end,
                                color: 'rgba(255,0,0,0.5)'});
                            setTimeout(() => {
                                animationRegion.setOptions({
                                    start: animationRegion.start,
                                    end: animationRegion.end,
                                    color: 'rgba(0,0,0,0)'});
                            }, 200);
                        } else {
                            animationRegion.setOptions({
                                start: animationRegion.start,
                                end: animationRegion.end,
                                color: 'rgba(0,0,0,0)'});
                        }
                        beforeRegion.setOptions({start: 0, end: animationRegion.start+0.001});
                        afterRegion.setOptions({start: animationRegion.end-0.001, end: totalDuration});
                        unstable_batchedUpdates(() => {
                            setAudioSegmentStart(animationRegion.start);
                            setAudioSegmentEnd(animationRegion.end);
                        });                                   

                        priorAnimationRegionPositionReference.start = animationRegion.start;
                        priorAnimationRegionPositionReference.end = animationRegion.end;

                        dragMarker.innerText = `← ${duration.toFixed(2)}s →`;
                    });

                    // Safety-net to catch animation duration being out of bounds
                    animationRegion.on('update-end', () => {
                        const duration = animationRegion.end - animationRegion.start;
                        if (duration < MIN_AUDIO_DURATION) {
                            if (animationRegion.start === 0) {
                                animationRegion.end = animationRegion.start + MIN_AUDIO_DURATION;
                            } else {
                                animationRegion.start = animationRegion.end - MIN_AUDIO_DURATION;
                            }
                        } else if (duration > maxAudioDurationForUser) {
                            if (animationRegion.start === 0) {
                                animationRegion.end = animationRegion.start + maxAudioDurationForUser;
                            } else {
                                animationRegion.start = animationRegion.end - maxAudioDurationForUser;
                            }
                        }
                        const newSegmentDuration = animationRegion.end - animationRegion.start;
                        animationRegion.setOptions({
                            start: animationRegion.start,
                            end: animationRegion.end,
                            color: 'rgba(0,0,0,0)'});
                        dragMarker.innerText = `← ${newSegmentDuration.toFixed(2)}s →`;

                        ws.seekTo(animationRegion.start / ws.getDuration());
                    });
                    
                    if (audioEventData) {
                        // Update regions from initial data 
                        audioEventData.forEach((event, idx) => {
                            wsRegionsRef.current?.addRegion({
                                start: event.time + (newAudioSegmentStart) + eventShift,
                                color: waveformColours.manualEventColour,
                                drag: true,
                                // Loaded events are always treated as manual.
                                // TODO - should we persist and restore which were manyal and which were detected?
                                id: `manual event ${idx}`,
                            });
                        })
                        updateEventStateFromRegionData();
                    }
                    unstable_batchedUpdates(() => {
                        setWs(ws);
                        setAudioTotalDuration(totalDuration);
                        setDetectedOnsetEventsToShow([]);
                        setAudioSegmentStart(newAudioSegmentStart);
                        setAudioSegmentEnd(newAudioSegmentEnd);
                        setVisibleDuration(totalDuration);
                    });
                    
                    ws.seekTo(newAudioSegmentStart / ws.getDuration());
                }}
                onDblclick={(ws : WaveSurfer, x : number) => {
                    if (!enableEvents) {
                        return;
                    }
                    const seconds = x * ws.getDuration();
                    wsRegionsRef.current?.addRegion({
                        start: seconds,
                        color: waveformColours.manualEventColour,
                        drag: true, 
                        id: `manual event ${wsRegionsRef.current?.getRegions().length ?? 0}`,
                    });
                    updateEventStateFromRegionData();
                }}
                onClick={(ws : WaveSurfer, x : number) => {
                    switch (isCalibratingTempo) {
                        case 1:
                            setTempoCalibrationPos1(x * ws.getDuration());
                            setIsCalibratingTempo(2);
                            break;
                        case 2:
                            const tempoCalibrationPos2 = x * ws.getDuration()
                            if (tempoCalibrationPos2 == tempoCalibrationPos1) {
                                setIsCalibratingTempo(0);
                                break;
                            }
                            const bpm = Math.max(10, Math.min(60 / Math.abs(tempoCalibrationPos2 - tempoCalibrationPos1), 350));
                            const offset = Math.min(tempoCalibrationPos1, tempoCalibrationPos2) % (60 / bpm);
                            setAudioBeatData({
                                offset: offset,
                                bpm: bpm
                            });
                            setIsCalibratingTempo(0);
                            break;
                    }
                }}

                onScroll={(ws, visibleStart, visibleEnd) => {
                    setVisibleTime({start: visibleStart, end: visibleEnd});
                    // ensure animationRegion is only draggable if at least one handle is visible
                    const animationRegion = wsRegionsRef.current?.getRegions().find(r => r.id === 'animation');
                    if (animationRegion) {
                        animationRegion.drag = visibleStart <= animationRegion.start || visibleEnd >= animationRegion.end;
                    }
                    setVisibleDuration(visibleEnd - visibleStart);
                }}

                onZoom={(ws, minPxPerSec) => {
                    const visiblePixels = ws.getWrapper().parentElement?.clientWidth
                    if (!visiblePixels || !isNumber(audioSegmentStart) || !isNumber(audioSegmentEnd)) {
                        return;
                    }
                    const visibleStart =  ws.getScroll() / minPxPerSec;
                    const visibleEnd = (ws.getScroll() + visiblePixels) / minPxPerSec;
                    setVisibleTime({start: visibleStart, end: visibleEnd});
                    setVisibleDuration(visibleEnd - visibleStart);
                }}
            />
        </div>
        <div className='flex flex-col space-y-5 lg:flex-row lg:space-x-5 lg:space-y-0 w-full '>
            <div className='flex flex-col w-full'>
                <div className='flex flex-row items-center space-x-5 mb-4 mt-4'>
                    <div className='font-extrabold'>Beats</div>
                        <div className='py-2 px-2' ref={beatIndicatorRef}></div>                            
                        <Button
                            disabled={isEstimatingTempo||!audioFilename}
                            className='rounded-none items-center justify-center flex flex-row bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white my-1 mx-2 hover:scale-x-110 hover:ease-linear hover:duration-150'
                            onClick={(e:React.MouseEvent) => void estimateTempo(e)}>
                                Estimate BPM {isEstimatingTempo && <Spinner className='inline ml-2 h-4 w-4' /> }
                        </Button>
                        <Button
                            disabled={isEstimatingTempo}
                            className='rounded-none items-center justify-center flex flex-row bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white my-1 mx-2 hover:scale-x-110 hover:ease-linear hover:duration-150'
                            onClick={() => setIsCalibratingTempo((calibrating) => calibrating ? 0 : 1 )}
                            >
                            { (isCalibratingTempo) == 0
                                ? 'Calibrate BPM'
                                : (isCalibratingTempo) == 1
                                ? 'Click 1st beat'
                                : 'Click 2nd beat'
                            }
                        </Button>
                    </div>
                { audioBeatData ?
                    (<div className={classNames('flex', 'flex-col', enableEvents ? 'md:flex-col' : 'md:space-x-6 md:flex-row')} >
                        <div className={classNames('', enableEvents ? '' : 'md:w-1/2')}>
                            <div className='mb-4'>
                                <Slider
                                    id="tempo"
                                    label="Tempo (bpm)"
                                    value={audioBeatData.bpm}
                                    setValue={(val) => {
                                        setAudioBeatData({
                                            ...audioBeatData ?? { offset: 0 },
                                            bpm: val
                                        });
                                    }}
                                    min={10}
                                    max={240}
                                    step={0.1}
                                    extraInfo=''
                                />
                            </div>
                        </div>
                        <div className={classNames('', enableEvents ? '' : 'md:w-1/2')}> 
                            <div className='mb-4'>
                                <Slider
                                    id="beatShift"
                                    label="Beat offset (s)"
                                    value={audioBeatData.offset}
                                    setValue={(val) => {
                                        setAudioBeatData({
                                            ...audioBeatData ?? { bpm: 120 },
                                            offset: val
                                        });
                                    }}
                                    min={-1}
                                    max={1}
                                    step={0.01}
                                    extraInfo={(audioBeatData && isNumber(audioSegmentStart))  ? '(' + firstBeatInSegment(audioBeatData.bpm, audioBeatData.offset, audioSegmentStart).beatOffset.toFixed(2) + 's)' : ''}
                                />
                            </div>
                        </div>
                    </div>) : audioFilename ? <div>Calculating BPM <Spinner />...</div> : <div>Uploading audio <Spinner />...</div>
                }
                
                
            </div>
            {enableEvents && <div className='flex flex-col w-full'>
                <div className='flex flex-row items-center space-x-5 mb-4 mt-4'>
                    <div className='font-extrabold'>Events</div>
                    <div  className='py-2 px-2' ref={eventIndicatorRef}></div>
                    <Button
                        disabled={isDetectingEvents||isEstimatingTempo||!audioFilename}
                        className='rounded-none items-center justify-center flex flex-row bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white my-1 mx-2 hover:scale-x-110 hover:ease-linear hover:duration-150'
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        onClick={(e : React.MouseEvent) => e.shiftKey ? void estimateEvents('energy') : void estimateEvents()}>
                            Detect Events {isDetectingEvents  && <Spinner className='inline ml-2 h-4 w-4' /> }
                    </Button>
                    <Button
                        disabled={isDetectingEvents}
                        className='rounded-none items-center justify-center flex flex-row bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white my-1 mx-2 hover:scale-x-110 hover:ease-linear hover:duration-150'
                        onClick={() => {
                            unstable_batchedUpdates(() => {
                                setDetectedOnsetEventsToShow([]);
                                setDetectedOnsetsEventsToIgnore([]);
                                setDetectedOnsetsSortedByStrength([]);
                                wsRegionsRef.current?.getRegions().filter(r => r.id.includes('event')).forEach(r => r.remove());
                                updateEventStateFromRegionData();
                            });                                            
                        }}>
                        Clear events
                    </Button>
                </div>
                <div className='mb-4'>
                    <Slider
                        id="eventSensitivity"
                        label="Event sensitivity"
                        value={eventSensitivity}
                        setValue={setEventSensitivity}
                        min={0}
                        max={1.0}
                        step={0.01}
                        extraInfo={`(${detectedEventCount} events)`}
                    />
                </div>
                <div className='mb-4'>
                    <Slider
                        id="eventShift"
                        label="Shift detected events (s)"
                        value={eventShift}
                        setValue={setEventShift}
                        min={-0.5}
                        max={0.5}
                        step={0.01}
                        extraInfo=''
                    />
                </div>
            </div>}
        </div>
    </div>;

    if (!audioUrl) {
        return <></>
    }
    
    if (isMobile) {
        return (
            <>
                {isMobileAudioEditorOpen && createPortal(
                    <div className="fixed inset-0 dark:bg-black bg-white z-50 flex justify-center items-center">
                        <button
                            className="z-50 absolute rounded-none top-1 right-1 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 opacity-95 border border-solid dark:border-white border-black"
                            onClick={() => {
                                setIsMobileAudioEditorOpen(false);
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                if (window.history.state && window.history.state.modal) {
                                    window.history.back();
                                }                                
                            }}
                        >
                            Close audio view
                        </button>
                        <div className="w-full h-full overflow-auto p-4">
                            {content}
                        </div>
                    </div>,
                    document.body
                )}
            </>
        );
    }
    
    return content;

};

export default AnnotatedWaveform; 